import React, { useState, useRef, useEffect, useMemo } from 'react';
import { XMarkIcon, CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { authAPI, dashboardAPI } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { canonicalCityName } from '../../utils/cityNormalizer';

/**
 * Build the dashboard-bundle params for a clinic so a prefetch produces the
 * SAME server cache key the real fetch will use after switching to it.
 * Mirrors the axios interceptor + Dashboard.js governmentFilter logic:
 *   - always scope by organization_ids
 *   - include organization_type / organization_city when the clinic has them
 *     (switchOrganization stores the canonicalized city)
 *   - government clinics also send source='combined' (Dashboard's default)
 */
const clinicBundleParams = (org) => {
  const params = { organization_ids: String(org.organization_id) };
  const type = org.organization_type || null;
  if (type) params.organization_type = type;
  const city = org.organization_city ? canonicalCityName(org.organization_city) : null;
  if (city) params.organization_city = city;
  if (type && type.toLowerCase() === 'government' && city) params.source = 'combined';
  return params;
};

function ClinicLogo({ org }) {
  const [errored, setErrored] = useState(false);
  const initial = (org.organization_name || '?').trim().charAt(0).toUpperCase();

  if (org.logo && !errored) {
    return (
      <img
        src={org.logo}
        alt={org.organization_name || 'Clinic logo'}
        className="w-11 h-11 rounded-full object-cover flex-shrink-0 border border-gray-100"
        onError={() => setErrored(true)}
      />
    );
  }
  return (
    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white text-base font-bold flex-shrink-0">
      {initial}
    </div>
  );
}

/**
 * Above this many clinics the list gets a filter box. An ordinary professional
 * works at a handful; a global admin gets every clinic on the platform.
 */
const SEARCH_THRESHOLD = 8;

function SwitchClinicModal({ isOpen, onClose }) {
  const { getProfessionalId, activeOrganizationId, switchOrganization, isGlobalAdmin } = useAuth();
  const modalRef = useRef(null);

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [switching, setSwitching] = useState(false);
  const [query, setQuery] = useState('');
  // Org ids we've already kicked off a cache-warming prefetch for, so re-opening
  // the modal (or hovering repeatedly) doesn't fire duplicate bundle requests.
  const prefetchedRef = useRef(new Set());
  // Warming is strictly opportunistic, so it must never compete with the
  // dashboard the user is looking at. One bundle already fans out to ~5
  // concurrent section queries against a 15-connection pool, so more than one
  // in flight is enough to exhaust it and turn a sub-second dashboard into a
  // ten-second one. Extra clinics queue instead of being dropped: dragging the
  // pointer down the list still warms everything it passed over, just serially.
  const prefetchQueueRef = useRef([]);
  const prefetchInFlightRef = useRef(0);
  const PREFETCH_MAX_IN_FLIGHT = 1;

  const drainPrefetchQueue = () => {
    while (
      prefetchInFlightRef.current < PREFETCH_MAX_IN_FLIGHT &&
      prefetchQueueRef.current.length > 0
    ) {
      const org = prefetchQueueRef.current.shift();
      prefetchInFlightRef.current += 1;
      dashboardAPI
        .prefetchBundle(clinicBundleParams(org))
        .catch(() => {})
        .finally(() => {
          prefetchInFlightRef.current -= 1;
          drainPrefetchQueue();
        });
    }
  };

  // Fire-and-forget: warm the server-side dashboard bundle cache for a clinic so
  // that selecting it (which reloads the app) lands on a warm cache instead of a
  // cold cross-region recompute. Errors are ignored — this is best-effort.
  //
  // Driven by pointer/keyboard focus on a single clinic row, never by the whole
  // list at once: warming every clinic on open put fifteen bundles in flight and
  // starved the dashboard the user was actually waiting on.
  const warmClinic = (org) => {
    if (!org || !org.organization_id) return;
    const key = String(org.organization_id);
    if (prefetchedRef.current.has(key)) return;
    if (key === String(activeOrganizationId)) return; // current clinic already loaded
    prefetchedRef.current.add(key);
    prefetchQueueRef.current.push(org);
    drainPrefetchQueue();
  };

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const load = async () => {
      const professionalId = getProfessionalId();
      if (!professionalId) {
        setError('No professional account found for this session.');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const res = await authAPI.getMyOrganizations(professionalId);
        if (cancelled) return;
        const orgs = Array.isArray(res.data) ? res.data : [];
        setOrganizations(orgs);
      } catch (err) {
        console.error('Failed to load organizations:', err);
        if (!cancelled) setError('Failed to load clinics. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      // Closing the modal withdraws the intent behind any clinic still queued.
      prefetchQueueRef.current = [];
    };
  }, [isOpen, getProfessionalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A stale filter would hide clinics that are still there next time the modal
  // opens, so reset it on close.
  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const showSearch = organizations.length > SEARCH_THRESHOLD;

  const filteredOrganizations = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return organizations;
    return organizations.filter((org) =>
      (org.organization_name || '').toLowerCase().includes(term)
    );
  }, [organizations, query]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  const handleSelect = (org) => {
    if (switching) return;
    if (String(org.organization_id) === String(activeOrganizationId)) {
      onClose();
      return;
    }
    setSwitching(true);
    switchOrganization(org);
    // Navigate to the landing page for the clinic's own type rather than reloading in
    // place: a government clinic picked from the private Clinic Overview would other-
    // wise reload straight back onto it. assign() still does a full document load, so
    // every org-scoped view refetches with the newly selected clinic.
    const type = (org.organization_type || '').toLowerCase();
    window.location.assign(type === 'private' ? '/clinic-overview' : '/dashboard');
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-xl font-bold text-gray-900">Switch to another clinic</h2>
            {isGlobalAdmin && (
              <span
                className="flex-shrink-0 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-700"
                title="You have platform-wide access to every clinic"
              >
                Global Admin
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {showSearch && (
          <div className="px-6 pb-4">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search clinics"
                aria-label="Search clinics"
                className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-[#2492B6] focus:outline-none"
              />
            </div>
          </div>
        )}

        <div className="border-t border-gray-100" />

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-6 py-8 text-center text-sm text-gray-500">Loading clinics...</div>
          )}

          {!loading && error && (
            <div className="px-6 py-8 text-center text-sm text-red-500">{error}</div>
          )}

          {!loading && !error && organizations.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-gray-500">
              No clinics found for your account.
            </div>
          )}

          {!loading && !error && organizations.length > 0 && filteredOrganizations.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-gray-500">
              No clinics match &ldquo;{query.trim()}&rdquo;.
            </div>
          )}

          {!loading && !error && filteredOrganizations.map((org) => {
            const isActive = String(org.organization_id) === String(activeOrganizationId);
            return (
              <button
                key={org.organization_id}
                onClick={() => handleSelect(org)}
                onMouseEnter={() => warmClinic(org)}
                onFocus={() => warmClinic(org)}
                disabled={switching}
                className="flex items-center gap-4 w-full px-6 py-4 text-left border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <ClinicLogo org={org} />
                <span
                  className={`flex-1 min-w-0 text-base font-medium ${
                    isActive ? 'text-[#2492B6]' : 'text-gray-700'
                  }`}
                >
                  {org.organization_name || 'Unnamed clinic'}
                </span>
                {isActive && (
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2492B6] flex items-center justify-center">
                    <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default SwitchClinicModal;
